/**
 * AIS WebSocket vessel position stream data source.
 * Connects to AISStream.io for real-time vessel position data.
 * Requires VITE_AIS_API_KEY. Uses WebSocket for streaming updates.
 *
 * The source manages its own WebSocket connection lifecycle:
 *   - Connects on start(), disconnects on stop()
 *   - Accumulates position updates in a buffer
 *   - fetch() drains the buffer and returns accumulated events
 *   - Auto-reconnects on disconnect with exponential backoff
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'ais-ws';

/** Maximum vessel positions to keep in buffer between fetches. */
const MAX_BUFFER_SIZE = 2000;

/** WebSocket reconnect backoff parameters. */
const RECONNECT_BASE_MS = 5_000;
const RECONNECT_MAX_MS = 120_000;

interface AisPositionMessage {
  MessageType: string;
  MetaData: {
    MMSI: number;
    ShipName?: string;
    latitude: number;
    longitude: number;
    time_utc: string;
  };
  Message: {
    PositionReport?: {
      Sog?: number;    // Speed over ground (knots)
      Cog?: number;    // Course over ground (degrees)
      TrueHeading?: number;
      NavigationalStatus?: number;
    };
    ShipStaticData?: {
      Type?: number;
      Name?: string;
      CallSign?: string;
      ImoNumber?: number;
      Destination?: string;
    };
  };
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

let ws: WebSocket | null = null;
let positionBuffer: NormalizedEvent[] = [];
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let apiKey: string | null = null;
let wsEndpoint: string | null = null;

function clearReconnectTimer(): void {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect(): void {
  clearReconnectTimer();
  const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, reconnectAttempts), RECONNECT_MAX_MS);
  reconnectAttempts++;
  console.log(`[Sentinel] AIS WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttempts})`);
  reconnectTimer = setTimeout(() => connectWebSocket(), delay);
}

function connectWebSocket(): void {
  if (!wsEndpoint || !apiKey) return;
  if (status.state === 'disabled') return;

  try {
    ws = new WebSocket(wsEndpoint);

    ws.onopen = () => {
      console.log('[Sentinel] AIS WebSocket connected');
      reconnectAttempts = 0;
      status.state = 'active';

      // Subscribe to position reports (global bounding box)
      const subscribeMsg = JSON.stringify({
        APIKey: apiKey,
        BoundingBoxes: [[[-90, -180], [90, 180]]],
        FilterMessageTypes: ['PositionReport'],
      });
      ws?.send(subscribeMsg);
    };

    ws.onmessage = (event) => {
      try {
        const data: AisPositionMessage = JSON.parse(event.data as string);
        if (!data.MetaData) return;

        const meta = data.MetaData;
        const pos = data.Message?.PositionReport;

        if (!isFinite(meta.latitude) || !isFinite(meta.longitude)) return;
        if (meta.latitude === 0 && meta.longitude === 0) return; // Invalid position

        const normalized: NormalizedEvent = {
          id: `ais-${meta.MMSI}`,
          source: SOURCE_ID,
          timestamp: meta.time_utc ?? new Date().toISOString(),
          type: 'vessel_position',
          title: meta.ShipName?.trim() || `MMSI ${meta.MMSI}`,
          severity: 'info',
          location: {
            lat: meta.latitude,
            lng: meta.longitude,
            name: meta.ShipName?.trim() || `MMSI ${meta.MMSI}`,
          },
          tags: ['ais', 'vessel', 'real-time'],
          raw: {
            mmsi: meta.MMSI,
            sog: pos?.Sog,
            cog: pos?.Cog,
            heading: pos?.TrueHeading,
            navStatus: pos?.NavigationalStatus,
          },
        };

        // Overwrite existing position for same MMSI (keep latest only)
        const existingIdx = positionBuffer.findIndex(e => e.id === normalized.id);
        if (existingIdx >= 0) {
          positionBuffer[existingIdx] = normalized;
        } else {
          positionBuffer.push(normalized);
          // Trim buffer if it grows too large
          if (positionBuffer.length > MAX_BUFFER_SIZE) {
            positionBuffer = positionBuffer.slice(-MAX_BUFFER_SIZE);
          }
        }
      } catch {
        // Skip malformed messages
      }
    };

    ws.onclose = () => {
      console.log('[Sentinel] AIS WebSocket closed');
      ws = null;
      if (status.state !== 'disabled') {
        status.state = 'stale';
        scheduleReconnect();
      }
    };

    ws.onerror = (event) => {
      console.error('[Sentinel] AIS WebSocket error:', event);
      status.state = 'error';
      status.lastError = 'WebSocket connection error';
    };
  } catch (err) {
    console.error('[Sentinel] AIS WebSocket connect failed:', err);
    status.state = 'error';
    status.lastError = err instanceof Error ? err.message : 'Connection failed';
    scheduleReconnect();
  }
}

export const aisStreamSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'AIS WebSocket',
    description: 'Real-time AIS vessel position stream',
    icon: 'anchor',
    category: 'maritime',
    updateInterval: 5_000, // 5 seconds — fetch drains the buffer
    requiresApiKey: true,
    dataLicense: 'AIS Provider Terms',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [
    {
      key: 'aisApiKey',
      label: 'AIS Stream API Key',
      type: 'password',
      required: true,
      helpText: 'API key for AISStream.io WebSocket connection',
    },
  ] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
    positionBuffer = [];
  },

  async start(): Promise<void> {
    // Fetch connection credentials from the proxy
    try {
      const base = getApiBase();
      const response = await fetch(`${base}/api/data/ais-stream`, {
        signal: AbortSignal.timeout(5_000),
      });

      if (response.ok) {
        const data = await response.json() as { endpoint: string; apiKey: string };
        wsEndpoint = data.endpoint;
        apiKey = data.apiKey;
        connectWebSocket();
      } else {
        console.warn('[Sentinel] AIS stream credentials not available, source will be inactive');
        status.state = 'error';
        status.lastError = 'AIS API key not configured';
      }
    } catch {
      console.warn('[Sentinel] AIS stream source not available (no API key)');
      status.state = 'error';
      status.lastError = 'Failed to fetch AIS credentials';
    }

    console.log('[Sentinel] AIS WebSocket source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
    clearReconnectTimer();
    if (ws) {
      ws.close();
      ws = null;
    }
    positionBuffer = [];
  },

  async fetch(): Promise<NormalizedEvent[]> {
    // Drain the buffer — return all accumulated positions
    const events = [...positionBuffer];
    status.lastFetch = new Date().toISOString();
    status.eventCount = events.length;
    return events;
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
